import type {
  SupplierOrderAllocationUnit,
  SupplierOrderItemAllocationPayload,
  SupplierOrderLinkPayload,
} from './api/supplierOrdersApi';

export interface SupplierOrderLinkFormValues {
  orderId?: string | null;
  allocateByItem?: boolean | null;
  supplierOrderItemId?: string | null;
  supplierOrderItemIndex?: number | null;
  allocatedQuantity?: number | null;
  allocatedUnit?: SupplierOrderAllocationUnit | null;
  allocatedCostPLN?: number | null;
  notes?: string | null;
  itemAllocations?: SupplierOrderItemAllocationPayload[];
}

interface BuildSupplierOrderLinkPayloadOptions {
  orderId?: string | null;
  canEditSupplierOrderFinance: boolean;
}

export function buildSupplierOrderLinkPayload(
  values: SupplierOrderLinkFormValues,
  options: BuildSupplierOrderLinkPayloadOptions,
): SupplierOrderLinkPayload {
  // Every new link that carries line allocations must persist exclusively
  // through OrderSupplierOrderItemAllocation.  Keeping the legacy header-level
  // item/quantity fields next to itemAllocations would create two competing
  // answers to "which customer line does this purchase cover?" and is exactly
  // what made transport-scoped PZ generation fail closed after linking from
  // some screens.
  if (Array.isArray(values.itemAllocations)) {
    if (values.itemAllocations.length === 0) {
      throw new Error('Wybierz co najmniej jedną odpowiadającą pozycję zamówienia klienta');
    }

    return {
      orderId: options.orderId || values.orderId || '',
      supplierOrderItemId: null,
      supplierOrderItemIndex: null,
      allocatedQuantity: null,
      allocatedUnit: null,
      allocatedCostPLN: null,
      notes: values.notes || null,
      itemAllocations: values.itemAllocations,
    };
  }

  const allocateByItem = values.allocateByItem === true;
  const basePayload = {
    orderId: options.orderId || values.orderId || '',
    supplierOrderItemId: allocateByItem ? values.supplierOrderItemId || null : null,
    supplierOrderItemIndex: allocateByItem ? values.supplierOrderItemIndex ?? null : null,
    allocatedCostPLN: allocateByItem && options.canEditSupplierOrderFinance
      ? values.allocatedCostPLN ?? null
      : null,
    notes: values.notes || null,
  };

  if (!allocateByItem || values.allocatedQuantity == null) {
    return {
      ...basePayload,
      allocatedQuantity: null,
      allocatedUnit: allocateByItem ? values.allocatedUnit || null : null,
    };
  }

  if (!values.allocatedUnit) {
    throw new Error('Jednostka alokacji jest wymagana, gdy podano przypisaną ilość');
  }

  return {
    ...basePayload,
    allocatedQuantity: values.allocatedQuantity,
    allocatedUnit: values.allocatedUnit,
  };
}
